import React, { useState, useEffect } from "react";
import { 
  Navigation, 
  MapPin, 
  AlertTriangle, 
  Sliders, 
  ShieldAlert, 
  Clock, 
  Car, 
  Layers, 
  Activity, 
  Compass
} from "lucide-react";

export default function DashboardMockup() {
  const [speed, setSpeed] = useState(62);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [mockSettings, setMockSettings] = useState({
    telegram: true,
    telemetry: true,
    sos: true,
    battery: false
  });

  const toggleSetting = (key) => {
    setMockSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Subtle speed fluctuation animation for active dashboard
  useEffect(() => {
    if (activeTab !== "dashboard") return;
    const interval = setInterval(() => {
      setSpeed(s => {
        const change = Math.floor(Math.random() * 5) - 2;
        const newSpeed = s + change;
        return newSpeed > 75 ? 75 : newSpeed < 50 ? 50 : newSpeed;
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const logs = [
    { time: "11:15:32 AM", event: "Engine Ignition On", type: "info" },
    { time: "11:14:05 AM", event: "Geofence Exit: Home Zone", type: "warning" },
    { time: "11:12:45 AM", event: "Speeding Alert: 74 km/h", type: "alert" },
    { time: "11:08:12 AM", event: "SOS System Ready", type: "success" }
  ];

  const renderMapContent = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <>
            {/* Geofence Zone Outline */}
            <circle cx="250" cy="90" r="45" fill="rgba(79, 70, 229, 0.03)" stroke="#4F46E5" strokeWidth="1.5" strokeDasharray="4 2" />
            
            {/* Route History Path */}
            <path className="animate-draw-route-short" d="M 30,100 L 150,100 L 180,60 L 260,60" fill="none" stroke="#4F46E5" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            
            {/* Start Marker */}
            <circle cx="30" cy="100" r="5" fill="#10B981" stroke="white" strokeWidth="2" />

            {/* Pulsing Vehicle Marker */}
            <g>
              <circle cx="260" cy="60" r="4" fill="#4F46E5">
                <animate attributeName="r" values="4;14" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="260" cy="60" r="5.5" fill="#4F46E5" stroke="white" strokeWidth="2" />
            </g>
          </>
        );
      case "trips":
        return (
          <>
            {/* Completed trip route */}
            <path className="animate-draw-route" d="M 30,100 L 150,100 L 180,60 L 320,60 L 340,160 L 380,160" fill="none" stroke="#7C3AED" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            
            {/* Start & End Markers */}
            <circle cx="30" cy="100" r="5.5" fill="#10B981" stroke="white" strokeWidth="2" />
            <circle cx="380" cy="160" r="5.5" fill="#dc2626" stroke="white" strokeWidth="2" />
            
            <text x="35" y="95" className="text-[8px] font-extrabold fill-slate-500 font-body">Start</text>
            <text x="365" y="150" className="text-[8px] font-extrabold fill-slate-500 font-body">End</text>

            {/* Pulsing Vehicle Marker at end */}
            <g>
              <circle cx="380" cy="160" r="4" fill="#7C3AED">
                <animate attributeName="r" values="4;14" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="380" cy="160" r="5.5" fill="#7C3AED" stroke="white" strokeWidth="2" />
            </g>
          </>
        );
      case "geofences":
        return (
          <>
            {/* Large active geofences */}
            <circle cx="200" cy="110" r="55" fill="rgba(6, 182, 212, 0.05)" stroke="#06B6D4" strokeWidth="2" />
            
            <circle cx="100" cy="130" r="35" fill="rgba(124, 58, 237, 0.03)" stroke="#7C3AED" strokeWidth="1.5" strokeDasharray="3 3" />
            <circle cx="100" cy="130" r="3" fill="#7C3AED" />
            
            <text x="175" y="78" className="text-[8px] font-extrabold fill-cyan-600 font-body">Office Safe Zone</text>
            <text x="82" y="112" className="text-[8px] font-bold fill-purple-600 font-body">Home Zone</text>

            {/* Pulsing center marker */}
            <g>
              <circle cx="200" cy="110" r="4" fill="#06B6D4">
                <animate attributeName="r" values="4;14" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="200" cy="110" r="5" fill="#06B6D4" stroke="white" strokeWidth="1.5" />
            </g>
          </>
        );
      case "alerts":
        return (
          <>
            {/* Warning Location Blink */}
            <circle cx="180" cy="60" r="28" fill="rgba(220, 38, 38, 0.08)" stroke="#dc2626" strokeWidth="1.5" strokeDasharray="3 2" />
            
            {/* Pulsing alert marker */}
            <g>
              <circle cx="180" cy="60" r="4" fill="#dc2626">
                <animate attributeName="r" values="4;14" dur="1.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0" dur="1.5s" repeatCount="indefinite" />
              </circle>
              <circle cx="180" cy="60" r="5.5" fill="#dc2626" stroke="white" strokeWidth="2" />
            </g>
          </>
        );
      case "settings":
        return (
          <>
            {/* Connection mesh animation */}
            <circle cx="200" cy="120" r="70" fill="none" stroke="rgba(79, 70, 229, 0.08)" strokeWidth="1" />
            <circle cx="200" cy="120" r="40" fill="none" stroke="rgba(79, 70, 229, 0.12)" strokeWidth="1" />
            
            {/* Pulsing settings connection marker */}
            <g>
              <circle cx="200" cy="120" r="4" fill="#4F46E5">
                <animate attributeName="r" values="4;14" dur="2.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0" dur="2.5s" repeatCount="indefinite" />
              </circle>
              <circle cx="200" cy="120" r="5.5" fill="#4F46E5" stroke="white" strokeWidth="2" />
            </g>
          </>
        );
      default:
        return null;
    }
  };

  const renderRightPanelContent = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <>
            <h4 className="text-xs font-bold text-slate-800 mb-3.5 flex items-center gap-1.5 pb-2 border-b border-slate-100 font-heading">
              <Activity size={12} className="text-indigo-600" />
              <span>Real-time Operations</span>
            </h4>
            
            <div className="space-y-3">
              {logs.map((log, index) => {
                let dotColor = "bg-blue-500";
                if (log.type === "warning") dotColor = "bg-yellow-500";
                if (log.type === "alert") dotColor = "bg-red-500";
                if (log.type === "success") dotColor = "bg-green-500";

                return (
                  <div key={index} className="text-[10px] leading-relaxed flex items-start gap-2.5 font-body">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 ${dotColor}`} />
                    <div>
                      <div className="font-bold text-slate-700">{log.event}</div>
                      <div className="text-[8.5px] text-slate-400 font-medium">{log.time}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      case "trips":
        return (
          <>
            <h4 className="text-xs font-bold text-slate-800 mb-3.5 flex items-center gap-1.5 pb-2 border-b border-slate-100 font-heading">
              <Clock size={12} className="text-purple-600" />
              <span>Trip Summary</span>
            </h4>
            <div className="space-y-3 font-body text-[10px]">
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                <div className="text-slate-400 font-bold uppercase text-[8px]">Route Taken</div>
                <div className="font-bold text-slate-850 mt-0.5">Connaught Place → Noida Sector 62</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                  <div className="text-slate-400 font-bold uppercase text-[8px]">Distance</div>
                  <div className="font-bold text-slate-850 mt-0.5">18.4 km</div>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                  <div className="text-slate-400 font-bold uppercase text-[8px]">Duration</div>
                  <div className="font-bold text-slate-850 mt-0.5">32 mins</div>
                </div>
              </div>
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                <div className="text-slate-400 font-bold uppercase text-[8px]">Safety Score</div>
                <div className="font-extrabold text-[#10B981] mt-0.5 flex items-center gap-1">
                  <span>94/100</span>
                  <span className="text-[8px] text-slate-450 font-semibold">(Excellent)</span>
                </div>
              </div>
            </div>
          </>
        );
      case "geofences":
        return (
          <>
            <h4 className="text-xs font-bold text-slate-800 mb-3.5 flex items-center gap-1.5 pb-2 border-b border-slate-100 font-heading">
              <Layers size={12} className="text-cyan-600" />
              <span>Active Zones</span>
            </h4>
            <div className="space-y-2.5 font-body text-[10px]">
              <div className="flex items-center justify-between p-2.5 rounded-lg border border-indigo-100 bg-indigo-50/50">
                <div>
                  <div className="font-bold text-slate-800">Home Safe Zone</div>
                  <div className="text-[8px] text-slate-400 mt-0.5">Radius: 150m</div>
                </div>
                <span className="text-[8px] font-extrabold text-indigo-650 bg-white border border-indigo-100 px-1.5 py-0.5 rounded">Inside</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-lg border border-cyan-105 bg-cyan-50/20">
                <div>
                  <div className="font-bold text-slate-800">Office Safe Zone</div>
                  <div className="text-[8px] text-slate-400 mt-0.5">Radius: 300m</div>
                </div>
                <span className="text-[8px] font-extrabold text-cyan-600 bg-white border border-cyan-100 px-1.5 py-0.5 rounded">Outside</span>
              </div>
              <button className="w-full py-2 border border-dashed border-slate-200 hover:border-indigo-500/40 text-slate-400 hover:text-indigo-600 rounded-lg text-center font-bold text-[9px] transition-colors mt-2">
                + Create New Zone
              </button>
            </div>
          </>
        );
      case "alerts":
        return (
          <>
            <h4 className="text-xs font-bold text-slate-800 mb-3.5 flex items-center gap-1.5 pb-2 border-b border-slate-100 font-heading">
              <AlertTriangle size={12} className="text-red-650" />
              <span>Critical Incidents</span>
            </h4>
            <div className="space-y-2.5 font-body text-[10px]">
              <div className="bg-red-50/50 border border-red-100 p-2.5 rounded-lg flex gap-2">
                <ShieldAlert size={14} className="text-red-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-red-950">SOS Triggered</div>
                  <div className="text-red-700 text-[8px] mt-0.5">Driver pressed hardware button</div>
                  <div className="text-[8px] text-slate-400 mt-1">11:12:45 AM</div>
                </div>
              </div>
              <div className="bg-amber-50/50 border border-amber-100 p-2.5 rounded-lg flex gap-2">
                <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-amber-950">Overspeed Breach</div>
                  <div className="text-amber-700 text-[8px] mt-0.5">Vehicle reached 102 km/h (Limit: 80)</div>
                  <div className="text-[8px] text-slate-400 mt-1">11:04:12 AM</div>
                </div>
              </div>
            </div>
          </>
        );
      case "settings":
        return (
          <>
            <h4 className="text-xs font-bold text-slate-800 mb-3.5 flex items-center gap-1.5 pb-2 border-b border-slate-100 font-heading">
              <Sliders size={12} className="text-slate-600" />
              <span>Device Settings</span>
            </h4>
            <div className="space-y-3.5 font-body text-[10px] pt-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-600">Telegram Bot Notifications</span>
                <button 
                  onClick={() => toggleSetting("telegram")}
                  className={`w-8 h-4.5 rounded-full relative transition-colors duration-200 focus:outline-none ${mockSettings.telegram ? 'bg-indigo-600' : 'bg-slate-200'}`}
                >
                  <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow-sm transition-all duration-200 ${mockSettings.telegram ? 'right-0.5' : 'left-0.5'}`}></span>
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-600">Real-time GPS Telemetry</span>
                <button 
                  onClick={() => toggleSetting("telemetry")}
                  className={`w-8 h-4.5 rounded-full relative transition-colors duration-200 focus:outline-none ${mockSettings.telemetry ? 'bg-indigo-600' : 'bg-slate-200'}`}
                >
                  <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow-sm transition-all duration-200 ${mockSettings.telemetry ? 'right-0.5' : 'left-0.5'}`}></span>
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-600">SOS Distress Actions</span>
                <button 
                  onClick={() => toggleSetting("sos")}
                  className={`w-8 h-4.5 rounded-full relative transition-colors duration-200 focus:outline-none ${mockSettings.sos ? 'bg-indigo-600' : 'bg-slate-200'}`}
                >
                  <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow-sm transition-all duration-200 ${mockSettings.sos ? 'right-0.5' : 'left-0.5'}`}></span>
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-600">Battery Saving Mode</span>
                <button 
                  onClick={() => toggleSetting("battery")}
                  className={`w-8 h-4.5 rounded-full relative transition-colors duration-200 focus:outline-none ${mockSettings.battery ? 'bg-indigo-600' : 'bg-slate-200'}`}
                >
                  <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow-sm transition-all duration-200 ${mockSettings.battery ? 'right-0.5' : 'left-0.5'}`}></span>
                </button>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-5xl rounded-2xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-200/50 overflow-hidden font-body transition-all duration-500 hover:shadow-indigo-500/5 hover:border-indigo-500/20">
      {/* Window Title Bar */}
      <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50/80 border-b border-slate-200/60 backdrop-blur-sm">
        <div className="flex gap-2">
          <span className="w-3 h-3 rounded-full bg-red-400 block" />
          <span className="w-3 h-3 rounded-full bg-yellow-400 block" />
          <span className="w-3 h-3 rounded-full bg-green-400 block" />
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 bg-slate-200/40 px-3 py-1 rounded-full border border-slate-200/30">
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] pulse-green" />
          <span>cartrack.io/dashboard</span>
        </div>
        <div className="w-14" />
      </div>

      {/* Main Layout */}
      <div className="flex flex-col md:flex-row min-h-[460px] bg-slate-50/30">
        
        {/* Mock Sidebar */}
        <aside className="w-full md:w-56 bg-slate-900 text-slate-300 p-5 flex flex-col justify-between border-r border-slate-800 shrink-0">
          <div>
            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              <img src="/logo192.png" alt="CarTrack Logo" className="w-8 h-8 rounded-lg shadow-md" />
              <span className="text-sm font-extrabold tracking-wide text-white font-heading">CarTrack</span>
            </div>

            {/* Menu Links */}
            <nav className="space-y-1">
              {[
                { id: "dashboard", label: "Dashboard", icon: Navigation },
                { id: "trips", label: "Trips History", icon: Clock },
                { id: "geofences", label: "Geofences", icon: Layers },
                { id: "alerts", label: "Alert Logs", icon: AlertTriangle },
                { id: "settings", label: "Device Settings", icon: Sliders }
              ].map(item => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                      isActive 
                        ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/10" 
                        : "hover:bg-slate-800 hover:text-white"
                    }`}
                  >
                    <Icon size={14} className={isActive ? "text-white" : "text-slate-400"} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Active Device Info */}
          <div className="mt-8 pt-4 border-t border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center">
                <Car size={13} className="text-indigo-400" />
              </div>
              <div className="overflow-hidden">
                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Active Device</div>
                <div className="text-xs font-semibold text-white truncate flex items-center gap-1.5">
                  <span>In-Car Headunit</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] pulse-green" />
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Mock Dashboard Area */}
        <main className="flex-1 p-6 flex flex-col gap-6 overflow-hidden">
          
          {/* Top Metric Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Card 1: Online Status */}
            <div className="bg-white border border-slate-200/80 rounded-xl p-4 flex items-center gap-3 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-[#10B981] flex items-center justify-center shrink-0">
                <Activity size={18} />
              </div>
              <div>
                <div className="text-[10px] text-slate-400 font-bold uppercase">Status</div>
                <div className="text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                  <span>Vehicle Online</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] pulse-green" />
                </div>
              </div>
            </div>

            {/* Card 2: GPS Status */}
            <div className="bg-white border border-slate-200/80 rounded-xl p-4 flex items-center gap-3 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                <Compass size={18} />
              </div>
              <div>
                <div className="text-[10px] text-slate-400 font-bold uppercase">GPS Core</div>
                <div className="text-xs font-extrabold text-slate-800">Active Tracking</div>
              </div>
            </div>

            {/* Card 3: Live Speed */}
            <div className="bg-white border border-slate-200/80 rounded-xl p-4 flex items-center gap-3 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-purple-50 text-[#7C3AED] flex items-center justify-center shrink-0">
                <Navigation size={18} className="transform rotate-45" />
              </div>
              <div>
                <div className="text-[10px] text-slate-400 font-bold uppercase">Live Speed</div>
                <div className="text-xs font-extrabold text-slate-800 tracking-tight transition-all duration-305">
                  {activeTab === "dashboard" ? `${speed} km/h` : activeTab === "trips" ? "0 km/h (Stopped)" : "62 km/h"}
                </div>
              </div>
            </div>

            {/* Card 4: SOS State */}
            <div className="bg-white border border-slate-200/80 rounded-xl p-4 flex items-center gap-3 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-cyan-50 text-[#06B6D4] flex items-center justify-center shrink-0">
                <ShieldAlert size={18} />
              </div>
              <div>
                <div className="text-[10px] text-slate-400 font-bold uppercase">Protection</div>
                <div className="text-xs font-extrabold text-[#06B6D4]">
                  {activeTab === "alerts" ? "SOS TRIGGERED" : "SOS System Ready"}
                </div>
              </div>
            </div>

          </div>

          {/* Map and Side Logs Grid */}
          <div className="grid lg:grid-cols-3 gap-6 flex-1 min-h-[260px]">
            
            {/* Simulated Map (Col-span 2) */}
            <div className="lg:col-span-2 rounded-xl border border-slate-200/80 bg-slate-100 relative overflow-hidden shadow-sm flex flex-col justify-between mock-map-grid">
              
              {/* Floating Map Zoom Toolbar */}
              <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md border border-slate-200/60 p-1.5 rounded-lg flex flex-col gap-1 shadow-md z-10">
                <button className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-100">+</button>
                <button className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-100">-</button>
              </div>

              {/* Map Route SVG Illustration (Simulated Interactive Map) */}
              <div className="absolute inset-0 z-0 flex items-center justify-center">
                <svg key={activeTab} className="w-full h-full opacity-60" viewBox="0 0 400 240">
                  {/* Grid Lines */}
                  <line x1="0" y1="60" x2="400" y2="60" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="0" y1="120" x2="400" y2="120" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="0" y1="180" x2="400" y2="180" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="100" y1="0" x2="100" y2="240" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="200" y1="0" x2="200" y2="240" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="300" y1="0" x2="300" y2="240" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
                  
                  {/* Standard Road Paths */}
                  <path d="M 0,100 L 150,100 L 180,60 L 320,60 L 340,160 L 400,160" fill="none" stroke="#e2e8f0" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M 0,100 L 150,100 L 180,60 L 320,60 L 340,160 L 400,160" fill="none" stroke="#f1f5f9" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
                  
                  {renderMapContent()}
                </svg>
              </div>


              {/* Floating Address Bar */}
              <div className="absolute bottom-3 left-3 right-3 bg-white/90 backdrop-blur-md border border-slate-200/60 p-2.5 rounded-xl flex items-center justify-between shadow-md z-10">
                <div className="flex items-center gap-2 overflow-hidden">
                  <MapPin size={13} className="text-indigo-600 shrink-0" />
                  <span className="text-[10px] font-bold text-slate-700 truncate">
                    {activeTab === "dashboard" ? "Connecting Ring Road, Sector 6, New Delhi" :
                     activeTab === "trips" ? "Completed Route: Connaught Place to Noida Sector 62" :
                     activeTab === "geofences" ? "Office Zone, Tech Park Lane, Bengaluru" :
                     activeTab === "alerts" ? "Distress Incident Triggered, Highway 4, Mumbai" :
                     "Device Core Status: Online"}
                  </span>
                </div>
                <span className="text-[9px] font-extrabold text-indigo-600 shrink-0 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">Live</span>
              </div>
            </div>

            {/* Sidebar Alert logs list */}
            <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm flex flex-col justify-between">
              {renderRightPanelContent()}

              <div className="pt-3 border-t border-slate-100 mt-4 flex items-center justify-between text-[9px] text-slate-400 font-bold">
                <span>System Health: 100%</span>
                <span className="text-indigo-600 hover:underline cursor-pointer">View Logs</span>
              </div>
            </div>

          </div>

        </main>
      </div>
    </div>
  );
}
